import React, { useEffect, useRef, useState } from "react";
import { supabase } from "./lib/supabase";
import { useAuth } from "./auth/AuthContext";
import { uploadAvatar, removeAvatar, upsertProfile } from "./lib/profiles";
import StudioStepTabs from "./components/StudioStepTabs";

const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

const SETTINGS_TABS = [
  { id: "settings", label: "Settings" },
  { id: "profile", label: "Profile" },
];

/** Fields we allow editing when present on the shared Atlas/Studio profiles row. */
const EDITABLE = [
  { key: "username", label: "Username" },
  { key: "display_name", label: "Display name" },
  { key: "full_name", label: "Full name" },
  { key: "bio", label: "Bio", multiline: true },
  { key: "website", label: "Website" },
];

function Avatar({ url, name }) {
  const initial = (name || "?").replace(/^@/, "").charAt(0).toUpperCase();
  return (
    <div className="profile-avatar" aria-hidden={!url}>
      {url ? (
        <img src={url} alt="" className="profile-avatar__img" />
      ) : (
        <span className="profile-avatar__fallback">{initial}</span>
      )}
    </div>
  );
}

export default function Profile() {
  const {
    user,
    updatePassword,
    updateEmail,
    reauthenticate,
    requestPasswordReset,
    passwordRecovery,
  } = useAuth();
  const fileInputRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [settingsMsg, setSettingsMsg] = useState("");
  const [settingsError, setSettingsError] = useState("");
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [resetSending, setResetSending] = useState(false);

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [removeAvatarFlag, setRemoveAvatarFlag] = useState(false);

  useEffect(() => {
    setEmail(user?.email || "");
  }, [user?.email]);

  useEffect(() => {
    if (passwordRecovery) {
      setActiveTab("settings");
      setSettingsMsg(
        "Choose a new password to finish resetting your account."
      );
      setSettingsError("");
    }
  }, [passwordRecovery]);

  useEffect(() => {
    return () => {
      if (avatarPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const { data, error: fetchError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .maybeSingle();

        if (fetchError) throw fetchError;

        if (!cancelled) {
          const row = data || { id: user.id, username: "" };
          setProfile(row);
          const next = { username: row.username ?? "" };
          EDITABLE.forEach(({ key }) => {
            if (key !== "username" && Object.prototype.hasOwnProperty.call(row, key)) {
              next[key] = row[key] ?? "";
            }
          });
          setForm(next);
          setAvatarFile(null);
          setAvatarPreview(null);
          setRemoveAvatarFlag(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load profile");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (user?.id) load();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const visibleFields = EDITABLE.filter(({ key }) => {
    if (key === "username") return true;
    return profile && Object.prototype.hasOwnProperty.call(profile, key);
  });

  const onChange = (key, value) => {
    setSaved(false);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setError("");
    setSaved(false);

    if (!file.type.startsWith("image/")) {
      setError("Avatar must be an image file.");
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setError("Avatar must be 5 MB or smaller.");
      return;
    }

    if (avatarPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(avatarPreview);
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setRemoveAvatarFlag(false);
  };

  const handleRemoveAvatar = () => {
    setSaved(false);
    setError("");
    if (avatarPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(avatarPreview);
    }
    setAvatarFile(null);
    setAvatarPreview(null);
    setRemoveAvatarFlag(true);
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSaved(false);

    try {
      const username = String(form.username || "").trim().replace(/^@+/, "");
      if (!username) {
        throw new Error("Username is required");
      }
      if (!/^[a-zA-Z0-9_]{3,30}$/.test(username)) {
        throw new Error("Username must be 3–30 characters (letters, numbers, underscore)");
      }

      let avatarUrl = profile?.avatar_url ?? null;

      if (removeAvatarFlag) {
        await removeAvatar(user.id);
        avatarUrl = null;
      } else if (avatarFile) {
        avatarUrl = await uploadAvatar(user.id, avatarFile);
      }

      const payload = {
        id: user.id,
        username,
        avatar_url: avatarUrl,
      };
      visibleFields.forEach(({ key }) => {
        if (key === "username") return;
        if (!Object.prototype.hasOwnProperty.call(profile || {}, key)) return;
        const val = typeof form[key] === "string" ? form[key].trim() : form[key];
        payload[key] = val === "" ? null : val;
      });

      const data = await upsertProfile(payload);

      setProfile(data);
      const next = { username: data.username ?? "" };
      EDITABLE.forEach(({ key }) => {
        if (key !== "username" && Object.prototype.hasOwnProperty.call(data, key)) {
          next[key] = data[key] ?? "";
        }
      });
      setForm(next);
      if (avatarPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(avatarPreview);
      }
      setAvatarFile(null);
      setAvatarPreview(null);
      setRemoveAvatarFlag(false);
      setSaved(true);
    } catch (err) {
      const code = err?.code || err?.status;
      const msg = String(err?.message || "");
      if (
        code === "23505" ||
        /duplicate key|unique constraint|profiles_username/i.test(msg)
      ) {
        setError("That username is already taken");
      } else {
        setError(err.message || "Failed to save profile");
      }
    } finally {
      setSaving(false);
    }
  };

  const onSaveEmail = async (e) => {
    e.preventDefault();
    setSettingsMsg("");
    setSettingsError("");

    const nextEmail = String(email || "").trim();
    const emailChanged =
      nextEmail.toLowerCase() !== String(user?.email || "").toLowerCase();

    if (!nextEmail) {
      setSettingsError("Email is required.");
      return;
    }
    if (!emailChanged) {
      setSettingsError("Enter a new email address to change it.");
      return;
    }
    if (!currentPassword) {
      setSettingsError("Enter your current password to confirm the change.");
      return;
    }

    setSettingsSaving(true);
    try {
      const { error: authError } = await reauthenticate(currentPassword);
      if (authError) throw authError;

      const { error: emailError } = await updateEmail(nextEmail);
      if (emailError) throw emailError;

      setCurrentPassword("");
      setSettingsMsg(
        "Confirmation sent. Check your inbox (and current email if Secure email change is on) before the new address takes effect."
      );
    } catch (err) {
      setSettingsError(err.message || "Failed to update email");
    } finally {
      setSettingsSaving(false);
    }
  };

  const onRequestPasswordReset = async () => {
    setSettingsMsg("");
    setSettingsError("");
    setResetSending(true);
    try {
      const { error: resetError } = await requestPasswordReset(user?.email);
      if (resetError) throw resetError;
      setSettingsMsg(
        "Password reset link sent. Open it from your email to choose a new password."
      );
    } catch (err) {
      setSettingsError(err.message || "Failed to send password reset email");
    } finally {
      setResetSending(false);
    }
  };

  const onSetNewPassword = async (e) => {
    e.preventDefault();
    setSettingsMsg("");
    setSettingsError("");

    if (newPassword.length < 6) {
      setSettingsError("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setSettingsError("Passwords do not match.");
      return;
    }

    setSettingsSaving(true);
    try {
      const { error: pwError } = await updatePassword(newPassword);
      if (pwError) throw pwError;
      setNewPassword("");
      setConfirmPassword("");
      setSettingsMsg("Password updated.");
    } catch (err) {
      setSettingsError(err.message || "Failed to update password");
    } finally {
      setSettingsSaving(false);
    }
  };

  const shownAvatarUrl = removeAvatarFlag
    ? null
    : avatarPreview || profile?.avatar_url || "";
  const displayName =
    form.display_name ||
    form.full_name ||
    form.username ||
    profile?.username ||
    "U";

  return (
    <div className="studio-layout studio-layout--solo">
      <div className="studio-panel">
        <div className="studio-panel__face">
        <StudioStepTabs
          step={activeTab}
          steps={SETTINGS_TABS}
          ariaLabel="Settings sections"
          onSelect={(id) => {
            setActiveTab(id);
            setSettingsMsg("");
            setSettingsError("");
            setError("");
            setSaved(false);
          }}
        />

        <div className="form-container">
        <div className="studio-form-body">
          {loading ? (
            <div className="panel-muted">Loading…</div>
          ) : (
            <div className="settings-tab-stack">
              <div
                className={`settings-tab-pane ${
                  activeTab === "settings" ? "settings-tab-pane--active" : ""
                }`}
                aria-hidden={activeTab !== "settings"}
              >
                <div className="title-form profile-form settings-panel">
                  {passwordRecovery ? (
                    <form onSubmit={onSetNewPassword}>
                      <div className="title-container">Set new password</div>
                      <p className="size-hint" style={{ margin: "0 0 0.75rem" }}>
                        You opened a password reset link. Choose a new password
                        below.
                      </p>
                      <label className="auth-field">
                        <div className="title-container">New password</div>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          autoComplete="new-password"
                          minLength={6}
                          required
                        />
                      </label>
                      <label className="auth-field">
                        <div className="title-container">Confirm password</div>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          autoComplete="new-password"
                          minLength={6}
                          required
                        />
                      </label>

                      {settingsError && (
                        <div className="studio-error">{settingsError}</div>
                      )}
                      {settingsMsg && !settingsError && (
                        <div className="studio-success">{settingsMsg}</div>
                      )}

                      <button
                        type="submit"
                        className="button button-primary"
                        disabled={settingsSaving}
                      >
                        {settingsSaving ? "Saving…" : "Update password"}
                      </button>
                    </form>
                  ) : (
                    <>
                      <form onSubmit={onSaveEmail}>
                        <label className="auth-field">
                          <div className="title-container">Email</div>
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              setSettingsMsg("");
                              setSettingsError("");
                            }}
                            autoComplete="email"
                            required
                          />
                        </label>
                        <label className="auth-field">
                          <div className="title-container">Current password</div>
                          <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                          />
                        </label>
                        <p className="size-hint" style={{ margin: "0 0 0.75rem" }}>
                          Changing email sends a confirmation link. The address
                          only updates after you confirm.
                        </p>

                        {settingsError && (
                          <div className="studio-error">{settingsError}</div>
                        )}
                        {settingsMsg && !settingsError && (
                          <div className="studio-success">{settingsMsg}</div>
                        )}

                        <button
                          type="submit"
                          className="button button-primary"
                          disabled={settingsSaving || resetSending}
                        >
                          {settingsSaving ? "Sending…" : "Send email confirmation"}
                        </button>
                      </form>

                      <div className="title-container" style={{ marginTop: 16 }}>
                        Change password
                      </div>
                      <p className="size-hint" style={{ margin: "0 0 0.75rem" }}>
                        We’ll email a reset link to{" "}
                        <strong>{user?.email || "your account"}</strong>. Your
                        password only changes after you open that link.
                      </p>
                      <button
                        type="button"
                        className="button button-secondary"
                        disabled={settingsSaving || resetSending}
                        onClick={onRequestPasswordReset}
                      >
                        {resetSending
                          ? "Sending…"
                          : "Email password reset link"}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div
                className={`settings-tab-pane ${
                  activeTab === "profile" ? "settings-tab-pane--active" : ""
                }`}
                aria-hidden={activeTab !== "profile"}
              >
                <form className="title-form profile-form" onSubmit={onSave}>
                  <div className="profile-avatar-wrap">
                    <Avatar url={shownAvatarUrl} name={displayName} />
                    <div className="profile-avatar-actions">
                      <button
                        type="button"
                        className="button button-secondary settings-btn"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {shownAvatarUrl ? "Change photo" : "Add photo"}
                      </button>
                      {shownAvatarUrl && (
                        <button
                          type="button"
                          className="button button-secondary settings-btn"
                          onClick={handleRemoveAvatar}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="size-hint">Image, max 5 MB</div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                  </div>

                  {visibleFields.map(({ key, label, multiline }) => (
                    <label key={key} className="auth-field">
                      <div className="title-container">{label}</div>
                      {multiline ? (
                        <textarea
                          value={form[key] ?? ""}
                          onChange={(e) => onChange(key, e.target.value)}
                          rows={3}
                        />
                      ) : (
                        <input
                          type="text"
                          value={form[key] ?? ""}
                          onChange={(e) => onChange(key, e.target.value)}
                          autoComplete="off"
                          required={key === "username"}
                        />
                      )}
                    </label>
                  ))}

                  {error && <div className="studio-error">{error}</div>}
                  {saved && !error && (
                    <div className="studio-success">Profile saved.</div>
                  )}

                  <button
                    type="submit"
                    className="button button-primary"
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save profile"}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
        </div>
        </div>
      </div>
    </div>
  );
}
